// src/components/pcr/sections/FieldDiagnosis.tsx
import React, { useRef, useEffect } from 'react';
import { useForm } from '../../../contexts/FormContext';
import SectionHeading from '../common/SectionHeading';

interface FieldDiagnosisProps {
  sectionHighlight?: string;
}

const FieldDiagnosis: React.FC<FieldDiagnosisProps> = ({ sectionHighlight }) => {
  const { formData, updateFormField } = useForm();
  const { fieldDiagnosis } = formData;

  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!sectionHighlight || !sectionRef.current) return;
    
    sectionRef.current.classList.add('section-highlight');
    sectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    
    setTimeout(() => {
      if (sectionRef?.current) {
        sectionRef.current.classList.remove('section-highlight');
      }
    }, 2000);
  }, [sectionHighlight]);

  const handleChange = (field: string, value: string) => {
    updateFormField('fieldDiagnosis', field, value);
  };

  const countWords = (text: string): number => {
    return text.trim().split(/\s+/).filter(Boolean).length;
  };

  return (
    <div ref={sectionRef} className="bg-gradient-to-br from-amber-50 to-white p-4 rounded-lg mb-6 shadow-md">
      <SectionHeading 
        title="Field Diagnosis" 
        description="This section is broken up into 3 steps, each with their own directions. Your general knowledge base and understanding of your role + scope of practice will determine how well you can answer these questions."
      />
      
      <div className="space-y-3">
        <div className="space-y-4">
          <div className="bg-white p-4 rounded-lg shadow-sm">
            <div className="flex justify-between items-center mb-1">
              <div className="flex items-baseline">
                <label htmlFor="fieldDiagnosis-step-2" className="block text-xl font-medium text-gray-700">Step 1</label>
                <span className="ml-2 text-sm text-gray-500">(200-400 words)</span>
              </div>
              <span className="text-xs text-gray-500">{countWords(fieldDiagnosis.step1)} words</span>
            </div>
            <p className="text-sm text-gray-600 italic mt-4 mb-2">
              Directions: Explain some of the obstacles you will face when trying to come to your field diagnosis for a multi-trauma patient. Exercise your critical thinking by illustrating both the obstacles you did, and did not face in this scenario.
            </p>
            <textarea 
              id="fieldDiagnosis-step-2" 
              className="text-xs md:text-base p-2 mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 min-h-[100px] overflow-auto" 
              value={fieldDiagnosis.step1}
              onChange={(e) => handleChange('step1', e.target.value)}
              aria-labelledby="label-fieldDiagnosis-step-2"
            />
          </div>
          
          <div className="bg-white p-4 rounded-lg shadow-sm">
            <div className="flex justify-between items-center mb-1">
              <div className="flex items-baseline">
                <label htmlFor="fieldDiagnosis-step-2" className="block text-xl font-medium text-gray-700">Step 2</label>
                <span className="ml-2 text-sm text-gray-500">(200-400 words)</span>
              </div>
              <span className="text-xs text-gray-500">{countWords(fieldDiagnosis.step2)} words</span>
            </div>
            <p className="text-sm text-gray-600 italic mt-4 mb-2">
              Directions: Demonstrate your knowledge by explaining why the signs & symptoms overlap, and how they differentiate.
            </p>
            <textarea 
              id="fieldDiagnosis-step-2" 
              className="text-xs md:text-base p-2 mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 min-h-[100px] overflow-auto" 
              value={fieldDiagnosis.step2}
              onChange={(e) => handleChange('step2', e.target.value)}
              aria-labelledby="label-fieldDiagnosis-step-2"
            />
          </div>
          
          <div className="bg-white p-4 rounded-lg shadow-sm">
            <div className="flex justify-between items-center mb-1">
              <div className="flex items-baseline">
                <label htmlFor="fieldDiagnosis-step-3" className="block text-xl font-medium text-gray-700">Step 3</label>
                <span className="ml-2 text-sm text-gray-500">(200-400 words)</span>
              </div>
              <span className="text-xs text-gray-500">{countWords(fieldDiagnosis.step3)} words</span>
            </div>
            <p className="text-sm text-gray-600 italic mt-4 mb-2">
              Directions: Based on all the signs & symptoms, your assessment, and your critical thinking skills, make a field diagnosis. Explain which protocol(s) and procedures you are preparing to implement.
            </p>
            <textarea 
              id="fieldDiagnosis-step-3" 
              className="text-xs md:text-base p-2 mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 min-h-[100px] overflow-auto" 
              value={fieldDiagnosis.step3}
              onChange={(e) => handleChange('step3', e.target.value)}
              aria-labelledby="label-fieldDiagnosis-step-3"
            />
          </div>
          
          <div className="bg-white p-4 rounded-lg shadow-sm">
            <div className="flex justify-between items-center mb-1">
              <div className="flex items-baseline">
                <label htmlFor="fieldDiagnosis-treatment-&-interventions" className="block text-xl font-medium text-gray-700">Treatment & Interventions</label>
                <span className="ml-2 text-sm text-gray-500">(250-500 words)</span>
              </div>
              <span className="text-xs text-gray-500">{countWords(fieldDiagnosis.treatmentAndInterventions)} words</span>
            </div>
            <p className="text-sm text-gray-600 italic mt-4 mb-2">
              Directions: Using nationally accepted protocol or AHA Protocols, explain the treatments you are administering, making sure to highlight the indications for all. Describe the patient's response to each treatment walk through each step of the protocol(s) you've chosen. Be specific for every intervention performed, detailing the proper methods, equipment sizes, and dosages.
            </p>
            <textarea 
              id="fieldDiagnosis-treatment-&-interventions" 
              className="text-xs md:text-base p-2 mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 min-h-[100px] overflow-auto" 
              value={fieldDiagnosis.treatmentAndInterventions}
              onChange={(e) => handleChange('treatmentAndInterventions', e.target.value)}
              aria-labelledby="label-fieldDiagnosis-treatment-&-interventions"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default FieldDiagnosis;